import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EmailVerificationOutcome, EmailVerificationResponse, PublicService} from 'web-angular-client';

import {ROUTES} from '../../../app.routes';
import {EnvironmentService} from '../../../sdk/service/environment.service';

/**
 * View state for the verify-email landing. Drives the template — each value maps to a single
 * render block (loading spinner, success confirmation, expired-link UX, invalid-link UX,
 * missing-token UX, network-error UX).
 */
export type VerifyEmailViewState =
    | 'LOADING'
    | 'VERIFIED'
    | 'EXPIRED'
    | 'INVALID'
    | 'MISSING_TOKEN'
    | 'ERROR';

@Component({
    selector: 'app-verify-email',
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.scss'],
    standalone: false
})
export class VerifyEmailComponent implements OnInit
{
    viewState: VerifyEmailViewState = 'LOADING';

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected publicService: PublicService,
        protected environmentService: EnvironmentService)
    {
    }

    /**
     * Logo override for the popup, sourced from the active brand's environment config: the eCommerce-gateway
     * wordmark where the brand ships one (Go), otherwise unset so the shared template falls back to the brand's
     * own logo (GetPaid, which has no gateway asset).
     */
    get logoOverrideSrc(): string | undefined
    {
        return this.environmentService.gatewayLogoSrc;
    }

    /**
     * Header copy for the public-page-template, picked per view state so each terminal screen
     * stays distinct (verified, expired, invalid, etc.) inside the shared chrome.
     */
    get headerText(): string
    {
        switch (this.viewState)
        {
            case 'LOADING':
                return 'Verifying your email…';
            case 'VERIFIED':
                return 'Woohoo! Your account is ready';
            case 'EXPIRED':
                return 'This link has expired';
            case 'INVALID':
                return 'This link is no longer valid';
            case 'MISSING_TOKEN':
                return 'Verification link incomplete';
            case 'ERROR':
                return 'Something went wrong';
        }
    }

    ngOnInit(): void
    {
        const token: string | undefined = this.route.snapshot.queryParamMap.get('token')?.trim();

        if (!token)
        {
            this.viewState = 'MISSING_TOKEN';
            return;
        }

        this.publicService.verifySignupEmail({token}).subscribe({
            next: (response: EmailVerificationResponse) => this.viewState = this.mapOutcome(response?.outcome),
            error: () => this.viewState = 'ERROR'
        });
    }

    public goToLogin(): void
    {
        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]);
    }

    public restartSignup(): void
    {
        this.router.navigate([ROUTES.PUBLIC.SIGNUP_ECOMMERCE.path]);
    }

    private mapOutcome(outcome: EmailVerificationOutcome | undefined): VerifyEmailViewState
    {
        switch (outcome)
        {
            case EmailVerificationOutcome.VERIFIED:
            case EmailVerificationOutcome.ALREADY_VERIFIED:
                return 'VERIFIED';
            case EmailVerificationOutcome.EXPIRED:
                return 'EXPIRED';
            case EmailVerificationOutcome.INVALID:
                return 'INVALID';
            default:
                return 'ERROR';
        }
    }
}
